// frontend/src/App.jsx
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import ScrollToTop from './components/ScrollToTop'  // ← Import ScrollToTop
import OnboardingGate from './components/OnboardingGate'

// Existing Marketing & Auth Pages
import Landing from './pages/Landing'
import Login from './pages/Login'
import Register from './pages/Register'
import RegistrationSuccess from './pages/RegistrationSuccess'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Profile from './pages/Profile'

// Protected Core Application Engine Views
import Dashboard from './pages/Dashboard'
import Analyze from './pages/Analyze'
import Plans from './pages/Plans'
import Pay from './pages/Pay'
import Results from './pages/Results'

// Compliance, Public, and Operational Support Interfaces
import Contact from './pages/Contact'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Disclaimer from './pages/Disclaimer'
import RefundPolicy from './pages/RefundPolicy'
import About from './pages/About'
import Terms from './pages/Terms'
import Support from './pages/Support'
import ZeroRetention from './pages/ZeroRetention'
import Enterprise from './pages/Enterprise'
import Success from './pages/Success'
import Cancel from './pages/Cancel'

// ── Admin Panel ──────────────────────────────────────────────────────
import AdminLayout from './components/AdminLayout'
import AdminDashboard from './pages/Admin/Dashboard'
import AdminUsers from './pages/Admin/Users'
import AdminPlans from './pages/Admin/Plans'
import AdminBilling from './pages/Admin/Billing'
import AdminSubmissions from './pages/Admin/Submissions'
import AdminSettings from './pages/Admin/Settings'

// ── Admin Path from Environment ────────────────────────────────────
const ADMIN_PATH = import.meta.env.VITE_ADMIN_PATH || 'gmc-secure-dashboard-7x9k2m'

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <ScrollToTop />  {/* ← Add ScrollToTop component */}

                {/* Onboarding agreement gate (brief v1.1). Mounted once here
                    rather than per-page: the agreement is required before
                    first use of any authenticated surface, and a single
                    mount point cannot be forgotten when pages are added.
                    Renders nothing for logged-out visitors and nothing once
                    the agreement has been accepted. */}
                <OnboardingGate />

                <Routes>
                    {/* Public Structural Marketing & Authentication Core */}
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/registration-success" element={<RegistrationSuccess />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Public Compliance, Informational & Lead Intake Anchors */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/zero-retention" element={<ZeroRetention />} />
                    <Route path="/enterprise" element={<Enterprise />} />
                    <Route path="/success" element={<Success />} />
                    <Route path="/cancel" element={<Cancel />} />

                    {/* ── Admin Panel (Custom Path from .env) ── */}
                    <Route path={`/${ADMIN_PATH}`} element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="plans" element={<AdminPlans />} />
                        <Route path="billing" element={<AdminBilling />} />
                        <Route path="submissions" element={<AdminSubmissions />} />
                        <Route path="settings" element={<AdminSettings />} />
                    </Route>

                    {/* Redirect from old /admin path (optional) */}
                    <Route path="/admin" element={<Navigate to={`/${ADMIN_PATH}`} replace />} />

                    {/* Protected Enterprise Compliance Workspace Nodes */}
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/analyze" element={<ProtectedRoute><Analyze /></ProtectedRoute>} />
                    <Route path="/plans" element={<ProtectedRoute><Plans /></ProtectedRoute>} />
                    <Route path="/pay" element={<ProtectedRoute><Pay /></ProtectedRoute>} />
                    <Route path="/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

                    {/* Fallback Catch-All Intercept Routing Strategy */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    )
}